import { Link } from 'react-router-dom';
import CategoryTag from '@/components/base/CategoryTag';
import { buildArticlePath, formatViews } from '@/utils/articles';
import type { Article } from '@/types';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'rank' | 'wide';
  rank?: number;
}

export default function ArticleCard({ article, variant = 'default', rank }: ArticleCardProps) {
  if (variant === 'rank') {
    return (
      <Link
        to={buildArticlePath(article.id)}
        className="group flex items-center gap-4 border-b border-background-200 py-3 last:border-b-0"
      >
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
            rank && rank <= 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-700'
          }`}
        >
          {rank}
        </span>
        <span className="line-clamp-2 flex-1 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
          {article.title}
        </span>
        <span className="hidden shrink-0 items-center gap-1 text-xs text-foreground-500 sm:flex">
          <i className="ri-eye-line" />
          {formatViews(article.views)}
        </span>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={buildArticlePath(article.id)}
        className="group flex gap-4 border-b border-background-200 py-4 last:border-b-0"
      >
        <div className="h-24 w-32 shrink-0 overflow-hidden rounded-lg bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} · 18爆料网`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-between">
          <div>
            <h3 className="line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
              {article.title}
            </h3>
            <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">{article.summary}</p>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <CategoryTag slug={article.category} name={article.categoryName} />
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'wide') {
    return (
      <Link
        to={buildArticlePath(article.id)}
        className="group grid grid-cols-1 gap-0 overflow-hidden rounded-xl border border-background-200 bg-background-100 sm:grid-cols-2"
      >
        <div className="h-56 w-full overflow-hidden bg-background-200 sm:h-full">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} · 18爆料网`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col justify-center gap-3 p-5 md:p-7">
          <div className="flex items-center gap-2">
            <CategoryTag slug={article.category} name={article.categoryName} />
            {article.hot && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                <i className="ri-fire-fill" />
                热
              </span>
            )}
          </div>
          <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-2xl">
            {article.title}
          </h3>
          <p className="line-clamp-3 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span className="h-3 w-px bg-background-300" />
            <span>{article.publishTime}</span>
            <span className="h-3 w-px bg-background-300" />
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={buildArticlePath(article.id)}
      className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
    >
      <div className="h-44 w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} · 18爆料网`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col gap-2.5 p-4 md:p-5">
        <div className="flex items-center gap-2">
          <CategoryTag slug={article.category} name={article.categoryName} />
          {article.hot && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
              <i className="ri-fire-fill" />
              热
            </span>
          )}
        </div>
        <h3 className="line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600 md:text-lg">
          {article.title}
        </h3>
        <p className="line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{article.date}</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line" />
              {article.comments}
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}